import { useState, useEffect } from 'react';
import { FileText, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { 
  Dialog, 
  DialogContent} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ModalHeader from '@/components/organisms/ModalHeader/ModalHeader';
import TermsContent from '@/components/organisms/TermsContent/TermsContent';
import PrivacyContent from '@/components/organisms/PrivacyContent/PrivacyContent';
import ModalFooter from '@/components/organisms/ModalFooter/ModalFooter';

interface TermsAndPrivacyModalProps {
  showButton?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  initialTab?: 'terms' | 'privacy';
}

export default function TermsAndPrivacyModal({ 
  showButton = true, 
  isOpen: externalIsOpen, 
  onOpenChange,
  initialTab = 'terms'
}: TermsAndPrivacyModalProps) {
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(initialTab);
    const { t, i18n } = useTranslation();
  
    // Use external control if provided, otherwise use internal state
    const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
    const setIsOpen = onOpenChange || setInternalIsOpen;
  
    const openModal = () => setIsOpen(true);
    const closeModal = () => setIsOpen(false);
    
    const toggleLanguage = () => {
      const newLang = i18n.language === 'es' ? 'en' : 'es';
      i18n.changeLanguage(newLang);
    };

    // Update active tab when initialTab changes
    useEffect(() => {
      setActiveTab(initialTab);
    }, [initialTab]);

    const handleTabChange = (value: string) => {
      if (value === 'terms' || value === 'privacy') {
        setActiveTab(value);
      }
    };
  
    return (
      <>
        {showButton && (
        <div className="flex gap-4 p-8">
          <Button onClick={openModal} className="gap-2 rounded-2xl" size="lg">
            <FileText className="w-5 h-5" />
              {t('termsAndPrivacyModal.openButton')}
          </Button>
        </div>
        )}
  
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="max-w-6xl w-[95vw] h-[90vh] flex flex-col p-0 rounded-3xl">
            <ModalHeader 
              language={i18n.language} 
              onLanguageToggle={toggleLanguage} 
              onClose={closeModal}
            />
            
            <Tabs value={activeTab} onValueChange={handleTabChange} className="flex-1 flex flex-col overflow-hidden">
              <TabsList className="grid w-full grid-cols-2 mx-4 sm:mx-6 mt-4 mb-4 shrink-0">
                <TabsTrigger value="terms" className="gap-1 sm:gap-2 text-xs sm:text-sm">
                  <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">{t('termsAndPrivacyModal.termsTab')}</span>
                  <span className="sm:hidden">Términos</span>
                </TabsTrigger>
                <TabsTrigger value="privacy" className="gap-1 sm:gap-2 text-xs sm:text-sm">
                  <Shield className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">{t('termsAndPrivacyModal.privacyTab')}</span>
                  <span className="sm:hidden">Privacidad</span>
                </TabsTrigger>
              </TabsList>
              
              <div className="flex-1 overflow-hidden px-4 sm:px-6">
                <TabsContent value="terms" className="h-full overflow-y-auto mt-0 data-[state=active]:mt-0 pr-2">
                  <TermsContent />
                  <div className="h-4"></div>
                </TabsContent>
                
                <TabsContent value="privacy" className="h-full overflow-y-auto mt-0 data-[state=active]:mt-0 pr-2">
                  <PrivacyContent />
                  <div className="h-4"></div>
                </TabsContent>
              </div>
            </Tabs>
            
            <ModalFooter onClose={closeModal} />
          </DialogContent>
        </Dialog>
      </>
    );
  }